<template>
  <lf-card
    class="mb-8 bg-white pt-6 pb-5"
    has-header-divider
    :has-header-margin="false"
    :has-padding="false"
    data-cy="funder-card"
  >
    <template #header>
      <div class="flex w-full justify-between px-6">
        <div class="flex items-center space-x-3">
          <icon-base
            v-if="isOfferStage(activeStage)"
            :icon="IconDragHandle"
            width="7"
            height="12"
            viewBox="0 0 12 20"
            class="drag-item -mt-1 mr-3 cursor-pointer"
          />
          <img v-if="logoUrl" :src="logoUrl" alt="logo" class="h-10 w-10" />
          <lf-h3>
            {{ sectionTitle }}
          </lf-h3>
        </div>
        <div
          v-if="groupedOffers.offer_details.length > 1 && !alwaysShowAll"
          class="w-[35vw]"
        >
          <lf-scrollable-menu
            class="flex justify-end"
            :styling-options="{
              offsetRightClasses: '-top-2 -right-12',
              offsetLeftClasses: '-top-2 -left-12',
              buttonsTransparent: true,
            }"
          >
            <button
              class="mx-0.5 rounded-2xl border px-2 py-1 text-xs font-medium uppercase"
              :class="
                selectedOfferType === OfferBlockType.all
                  ? 'border-primary bg-blue-100/60 text-primary'
                  : 'border-gray-200'
              "
              @click="changeSelectedOffer(OfferBlockType.all, 0)"
            >
              {{ $t("COMMON.ALL") }}
            </button>
            <button
              v-if="hasVisibleOffer"
              class="mx-0.5 rounded-2xl border px-2 py-1 text-xs font-medium uppercase"
              :class="
                selectedOfferType === OfferBlockType.visible
                  ? 'border-primary bg-blue-100/60 text-primary'
                  : 'border-gray-200'
              "
              @click="changeSelectedOffer(OfferBlockType.visible, 0)"
            >
              {{ $t("COMMON.VISIBLE") }}
            </button>
            <button
              v-for="(offerDetails, index) in groupedOffers.offer_details"
              :key="offerDetails.id"
              class="mx-0.5 rounded-2xl border px-2 py-1 text-xs font-medium uppercase"
              :class="
                selectedOfferType === OfferBlockType.index &&
                index === selectedOfferIndex
                  ? 'border-primary bg-blue-100/60 text-primary'
                  : 'border-gray-200'
              "
              @click="changeSelectedOffer(OfferBlockType.index, index)"
            >
              {{ $t("COMMON.OFFER") }} {{ index + 1 }}
            </button>
          </lf-scrollable-menu>
        </div>
      </div>
    </template>
    <div class="overflow-x-auto">
      <div
        v-if="groupedOffers.offer_details.length"
        class="inline-block min-w-full"
      >
        <div class="flex flex-col">
          <offer-table-header
            v-if="showBulkSelectHeader"
            :selected-offers="selectedOffers"
            :offers="filteredOffers"
            @bulk-selection-change="onBulkSelectionChange"
          />
          <offer-line-item
            v-for="(offerDetails, index) in filteredOffers"
            :class="{
              'border-t': index !== 0,
              'border-b': index === filteredOffers.length - 1,
            }"
            :key="offerDetails.id"
            :offer-detail="offerDetails"
            :funder="groupedOffers.funder"
            :placement="offerDetails.placement"
            :is-closing="isClosing"
            :is-funded="isFunded"
            :single-select="singleSelect"
            :multi-select="multiSelect"
            :select-one-key="selectOneKey"
            :select-details-provider="selectDetailsProvider"
            :view-only="viewOnly"
            class="shrink-0"
            :is-row-clickable="isRowClickable"
            :is-selected="selectedOffersIdsSet.has(offerDetails.id)"
            @selection-change="
              (offer, selected) =>
                $emit('updateSelectedOffers', [offer], selected)
            "
          />
        </div>
      </div>
      <div v-else class="p-2 text-center">
        {{ $t("OFFER_CUSTOMISATION.OFFERS_EMPTY") }}
      </div>
    </div>
  </lf-card>
</template>
<script setup lang="ts" generic="K extends IOffer">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { isOfferStage } from "@/helpers/workflow";
import { useActiveStage } from "@/hooks/workflow";

import OfferLineItem from "@/components/deals/workflow/OfferStage/WorkflowLive/OfferLineItem.vue";
import OfferTableHeader from "@/components/deals/workflow/OfferStage/WorkflowLive/OfferTableHeader.vue";
import IconDragHandle from "@/components/icons/IconDragHandle.vue";
import LfScrollableMenu from "@/components/ui/LfScrollableMenu.vue";

import type { SelectDetailsProvider } from "@/models/common";
import type { IOffer } from "@/models/funders";
import type { GroupedOffer } from "@/models/offers";

const props = defineProps<{
  groupedOffers: GroupedOffer<K>;
  soleFunder: boolean;
  isClosing?: boolean;
  isFunded?: boolean;
  isRowClickable?: boolean;
  multiSelect?: boolean;
  singleSelect?: boolean;
  selectOneKey?: keyof K;
  selectDetailsProvider?: SelectDetailsProvider;
  includeProductInTitle?: boolean;
  title?: string;
  alwaysShowAll?: boolean;
  viewOnly?: boolean;
  selectedOffers?: IOffer[];
  showBulkSelectHeader?: boolean;
}>();

const emit = defineEmits<{
  updateSelectedOffers: [IOffer[], boolean | string];
}>();

const { t } = useI18n();
const { activeStage } = useActiveStage();

enum OfferBlockType {
  all = "all",
  visible = "visible",
  index = "index",
}

const selectedOfferIndex = ref(0);
const selectedOfferType = ref<OfferBlockType>(OfferBlockType.all);

const selectedOffersIdsSet = computed(() => {
  return new Set(props.selectedOffers?.map(({ id }) => id));
});

const logoUrl = computed(() => {
  return props.groupedOffers.funder?.logo?.file_url;
});

const filteredOffers = computed(() => {
  if (selectedOfferType.value === OfferBlockType.visible) {
    return props.groupedOffers.offer_details.filter(
      ({ is_shared }) => is_shared
    );
  }

  if (selectedOfferType.value === OfferBlockType.index) {
    return [props.groupedOffers.offer_details[selectedOfferIndex.value]];
  }

  return props.groupedOffers.offer_details;
});

const hasVisibleOffer = computed(
  () =>
    props.groupedOffers.offer_details.filter(({ is_shared }) => is_shared)
      .length > 0
);

const sectionTitle = computed(() => {
  if (props.title) {
    return props.title;
  }
  let title = props.groupedOffers.funder?.full_name ?? t("COMMON.FUNDER");
  if (props.includeProductInTitle) {
    title += " ● " + props.groupedOffers.offer_details[0].product.name;
  }
  return title;
});

const changeSelectedOffer = (type: OfferBlockType, index: number) => {
  selectedOfferType.value = type;
  selectedOfferIndex.value = index;
};

const onBulkSelectionChange = (isSelected: boolean) => {
  emit("updateSelectedOffers", filteredOffers.value, isSelected);
};

watch(
  () => hasVisibleOffer.value,
  (value: boolean) => {
    if (!value && selectedOfferType.value === OfferBlockType.visible) {
      selectedOfferType.value = OfferBlockType.all;
    }
  }
);
</script>
